import axios from 'axios';
import { CURR_API } from '@/constants/apis';
import { refusalMessage } from '@/api/refusals';

/**
 * Texts and calls through keyon's Twilio branches (SendText, SendPhoneCall).
 *
 * Every caller used to read "sent" into any non-empty `status` - which keyon's
 * own error bodies have - and to leave a 4xx as an unhandled rejection, so a
 * refused or failed send either claimed success or left the button spinning.
 * These never throw: they answer {ok: true}, or {ok: false, message} with a
 * sentence to show - keyon's, when it has one (lib/TwilioAccess.php), else our
 * own.
 */
export const SMS_FAILED_MESSAGE = 'The text message could not be sent.';
export const CALL_FAILED_MESSAGE = 'The call could not be placed.';

const HEADERS = { headers: { 'Content-Type': 'application/json;' } };

/**
 * keyon answers {status: <Twilio sid>, TwilioStatus: "successful"} only when
 * Twilio accepted every message or the call; anything else did not go out.
 */
export function twilioAccepted(data) {
  return !!data
    && typeof data === 'object'
    && data.TwilioStatus === 'successful'
    && typeof data.status === 'string'
    && data.status.trim() !== '';
}

async function post(twilio, fallback) {
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', { Twilio: twilio }, HEADERS);
    const data = res && res.data;
    if (twilioAccepted(data)) return { ok: true };
    return { ok: false, message: refusalMessage(data) || fallback };
  } catch (err) {
    const data = err && err.response ? err.response.data : null;
    return { ok: false, message: refusalMessage(data) || fallback };
  }
}

/**
 * `patientId` names the recipient instead of the number: keyon's id for a
 * patient (allData.patient.externId). Under enforcement keyon then texts or
 * rings the number on its own record of that patient for the caller's
 * organization, whatever pacmny-be's profile says; the number is still sent,
 * for keyon to use until then.
 */
function withPatient(twilio, options) {
  const id = options && options.patientId != null ? String(options.patientId).trim() : '';
  return id ? { ...twilio, patient_unique_ID: id } : twilio;
}

/**
 * @param {string} to one number (keyon also takes a comma list, up to ten)
 * @param {string} message
 * @param {{patientId?: string}} [options]
 * @returns {Promise<{ok: true}|{ok: false, message: string}>}
 */
export function sendTextMessage(to, message, options = {}) {
  return post(withPatient({ API_Meth: 'SendText', smsnumber: to, smsmessage: message }, options), SMS_FAILED_MESSAGE);
}

/**
 * Ring `to` from the company line and bridge it to the caller.
 *
 * `from` is the number the caller asked to be connected to - their profile
 * phone, possibly empty. Under enforcement keyon connects the call to one of
 * the caller's own numbers on its record, this one when it is one of them, and
 * says what to do when it has none (keyon/lib/TwilioAccess.php).
 *
 * @param {{patientId?: string}} [options]
 * @returns {Promise<{ok: true}|{ok: false, message: string}>}
 */
export function placePhoneCall(to, from, options = {}) {
  return post(withPatient({ API_Meth: 'SendPhoneCall', callnumber: to, from }, options), CALL_FAILED_MESSAGE);
}
